import React, { useCallback, useMemo } from 'react';
import {
  PiArrowsCounterClockwiseDuotone,
  PiArrowsOutCardinalDuotone,
  PiArrowsOutSimpleDuotone,
  PiCaretDoubleLeftDuotone,
  PiCirclesThreePlusDuotone,
  PiCloudArrowDownDuotone,
  PiCopyDuotone,
  PiCursorTextDuotone,
  PiEraserDuotone,
  PiPencilDuotone,
  PiQuestionDuotone,
} from 'react-icons/pi';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { useTranslate } from '@spazious/dollhouse-i18n';
import { useHistory } from '@spazious/dollhouse-store';
import { styled } from '@spazious/dollhouse-theme';
import { Flex, IconButton } from '@spazious/dollhouse-uikit';

export const HistoryTimelineEntry = styled(Flex)`
  background-color: var(--spz-sys-color-surface-container);
  border-width: 2px;
  border-style: solid;
  border-color: var(--spz-sys-color-on-surface);
  border-radius: 50%;
  width: 48px;
  height: 48px;
  scroll-snap-align: start;
`;

export const commandIconMapping: Partial<Record<CommanderActionTypesEnum, React.ReactNode>> = {
  [CommanderActionTypesEnum.Init]: <PiCaretDoubleLeftDuotone />,
  [CommanderActionTypesEnum.Add]: <PiCirclesThreePlusDuotone />,
  [CommanderActionTypesEnum.Param]: <PiPencilDuotone />,
  [CommanderActionTypesEnum.Remove]: <PiEraserDuotone />,
  [CommanderActionTypesEnum.Translate]: <PiArrowsOutCardinalDuotone />,
  [CommanderActionTypesEnum.Rotate]: <PiArrowsCounterClockwiseDuotone />,
  [CommanderActionTypesEnum.Resize]: <PiArrowsOutSimpleDuotone />,
  [CommanderActionTypesEnum.Meta]: <PiCursorTextDuotone />,
  [CommanderActionTypesEnum.Duplicate]: <PiCopyDuotone />,
  [CommanderActionTypesEnum.Load]: <PiCloudArrowDownDuotone />,
};

export const HistoryTimeline: React.FC = () => {
  const history = useHistory();
  const translate = useTranslate();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const entries = useMemo(() => history.toArray(), [history.head]);

  const bindGoTo = useCallback(
    (hash: string) => () => {
      history.go(hash);
    },
    [history]
  );

  const entryNodes = useMemo(
    () =>
      entries.map(({ hash, meta }) => (
        <HistoryTimelineEntry key={hash}>
          <IconButton
            color={history.head.hash === hash ? 'primary' : undefined}
            data-history-hash={hash}
            title={translate('Click to go to this history entry')}
            onClick={bindGoTo(hash)}
          >
            {(meta ? (commandIconMapping as any)[meta] ?? null : null) ?? <PiQuestionDuotone />}
          </IconButton>
        </HistoryTimelineEntry>
      )),
    [bindGoTo, entries, history.head.hash, translate]
  );

  return <Flex gap="var(--spacing-gap)">{entryNodes}</Flex>;
};
